import type { Vector3 } from 'three';

import type { GeoLightingManager } from '../geo-lighting-manager.class';
import type { WeatherLightingPreset } from '../../../types';
import { WEATHER_PRESETS } from '../helpers';
import { celestialToPositionCfn } from './celestial-calculations.cfn';

/**
 * Update all lighting based on current settings
 */
export const updateLightingCfn = (
	self: GeoLightingManager
): void => {
	const state = self.state;
	const weatherPreset = WEATHER_PRESETS[self.weather];

	// Calculate light position
	const celestialPos = state.isNight && state.moonPosition
		? state.moonPosition
		: state.sunPosition;
	const lightPosition = celestialToPositionCfn(self, celestialPos);

	// Update ambient light
	updateAmbientLightCfn(self, weatherPreset, state.isNight);

	// Update directional light (sun/moon)
	updateDirectionalLightCfn(self, weatherPreset, lightPosition, state.isNight);

	// Update hemisphere light
	updateHemisphereLightCfn(self, weatherPreset, state.isNight);
};

/**
 * Update ambient light
 */
export const updateAmbientLightCfn = (
	self: GeoLightingManager,
	preset: WeatherLightingPreset,
	isNight: boolean
): void => {
	const { ambientLight } = self.configAccess;
	if (!ambientLight) return;

	const intensity = isNight ? preset.ambientIntensity * 0.3 : preset.ambientIntensity;
	ambientLight.color.setHex(preset.ambientColor);
	ambientLight.intensity = intensity;
};

/**
 * Update directional light (sun/moon)
 */
export const updateDirectionalLightCfn = (
	self: GeoLightingManager,
	preset: WeatherLightingPreset,
	position: Vector3,
	isNight: boolean
): void => {
	const { directionalLight } = self.configAccess;
	if (!directionalLight) return;

	const { sceneCenter } = self.internalAccess;

	// Position the light
	directionalLight.position.copy(position);
	directionalLight.target.position.copy(sceneCenter);

	// Color and intensity
	if (isNight) {
		// Moonlight is dimmer and bluish
		directionalLight.color.setHex(0xb0c4de); // Light steel blue
		directionalLight.intensity = preset.directionalIntensity * 0.15;
	} else {
		directionalLight.color.setHex(preset.directionalColor);
		directionalLight.intensity = preset.directionalIntensity;
	}

	// Shadows
	directionalLight.castShadow = preset.shadowsEnabled;
	if (directionalLight.shadow) {
		// Adjust shadow based on softness
		directionalLight.shadow.radius = preset.shadowSoftness * 8;
	}
};

/**
 * Update hemisphere light
 */
export const updateHemisphereLightCfn = (
	self: GeoLightingManager,
	preset: WeatherLightingPreset,
	isNight: boolean
): void => {
	const { hemisphereLight } = self.configAccess;
	if (!hemisphereLight) return;

	if (isNight) {
		// Night sky colors
		hemisphereLight.color.setHex(0x1a1a2e); // Dark blue
		hemisphereLight.groundColor.setHex(0x16213e); // Darker blue
		hemisphereLight.intensity = preset.hemisphereIntensity * 0.3;
	} else {
		hemisphereLight.color.setHex(preset.hemisphereSkyColor);
		hemisphereLight.groundColor.setHex(preset.hemisphereGroundColor);
		hemisphereLight.intensity = preset.hemisphereIntensity;
	}
};
